import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";
import { downloadReportCSV } from '../../lib/functions';

const WeeklyReport = ({ jobs, theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [weeklyData, setWeeklyData] = useState([]);
    const [monthlyTotal, setMonthlyTotal] = useState({ jobs: 0, cost: 0, noCost: 0, subbed: 0, subbedValue: 0 });

    const [selectedWeek, setSelectedWeek] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();

            } else {
                setWeeklyData([]);
                setMonthlyTotal({ jobs: 0, cost: 0, noCost: 0 });

                logger.log("Invalid password.");
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setOpenRequestPassword(false); // Close the modal
        }
    };

    const showData = () => {
        if (!jobs || jobs.length === 0) return;
        const groupedByWeek = {};
        let totalJobs = 0, totalCost = 0, totalNoCost = 0, totalSubbed = 0, totalSubbedValue = 0;

        jobs.forEach(job => {
            const { week, costings, is_subbed, subbed_price } = job;
            if (!groupedByWeek[week]) {
                groupedByWeek[week] = { week, totalJobs: 0, totalCost: 0, jobsWithoutCost: 0, totalSubbed: 0, totalSubbedValue: 0 };
            }
            groupedByWeek[week].totalJobs += 1;

            const numericCost = costings ? parseFloat(costings) : 0;
            if (numericCost > 0) {
                groupedByWeek[week].totalCost += numericCost;
                totalCost += numericCost;
            } else {
                groupedByWeek[week].jobsWithoutCost += 1;
                totalNoCost += 1;
            }
            totalJobs += 1;

            if (is_subbed) {
                groupedByWeek[week].totalSubbed += 1;
                totalSubbed += 1;
                const subbedVal = subbed_price ? parseFloat(subbed_price) : 0;
                groupedByWeek[week].totalSubbedValue += subbedVal;
                totalSubbedValue += subbedVal;
            }
        });

        setWeeklyData(Object.values(groupedByWeek).sort((a, b) => a.week - b.week));
        setMonthlyTotal({ jobs: totalJobs, cost: totalCost, noCost: totalNoCost, subbed: totalSubbed, subbedValue: totalSubbedValue });
    }

    // sort data and set selected week
    const handleSelectWeek = (week) => {
        if (week !== 'month') {
            const selectedJobs = jobs.filter(job => job.week === week);
            // sort by date
            selectedJobs.sort((a, b) => new Date(a.date) - new Date(b.date));
            setChartData(selectedJobs);
        } else {
            // sort by date
            jobs.sort((a, b) => new Date(a.date) - new Date(b.date));
            setChartData(jobs);
        }

        setSelectedWeek(week);
    };

    // sort data when filter selected
    const sortByColumn = (column) => {
        logger.log("Sorting by column:", column);
        const sortedData = chartData.sort((a, b) => {
            if (a[column] < b[column]) return -1;
            if (a[column] > b[column]) return 1;
            return 0;
        });
        setChartData([...sortedData]);
    };

    // filter by
    const handleFilter = (column, value) => {
        showAlert({
            message: `Filtering by ${column}: ${value}`,
            type: 'danger',
            buttons: [
                {
                    type: 'default',
                    label: 'Close'
                },
                {
                    type: 'primary',
                    label: 'Apply Filter',
                    onClick: () => filterData(column, value)
                },
            ],
        });
    };

    const filterData = (column, value) => {
        setIsFiltered(true);
        const filteredData = chartData.filter(item => {
            return item[column] === value;
        }
        );
        setChartData([...filteredData]);
    }

    const removeFilter = () => {
        if (selectedWeek === 'month') {
            setChartData(jobs);
        } else {
            handleSelectWeek(selectedWeek);
        }
        setIsFiltered(false);
    };

    const isLightTheme = theme === "light";

    return (
        <div id={theme} style={{ 
            display: "flex", 
            backgroundColor: isLightTheme ? "#fff" : '#080808', 
            // color: isLightTheme ? "#333" : "#ddd",
            minHeight: "100vh",
            padding: "20px",
            justifyContent: "center"
        }}>
            {/* show detailed week data */}
            {selectedWeek && selectedWeek !== 'month' && (
                <div style={{
                    flex: 1,
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    backgroundColor: isLightTheme ? '#f0f0f0' : '#2a2a2a',
                    borderRadius: '10px',
                    boxShadow: isLightTheme 
                        ? '0px 4px 8px rgba(0, 0, 0, 0.1)' 
                        : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                    maxWidth: '70vw',
                    margin: '20px auto',
                    border: isLightTheme ? '1px solid #ddd' : '1px solid #444'
                }}>
                        <h3
                        onClick={() => isFiltered ? removeFilter() : setSelectedWeek(null) }
                        style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: isLightTheme ? '#333' : '#fff',
                            marginBottom: '15px',
                            cursor: 'pointer',
                            justifyContent: 'space-between',
                        }}>
                            📊 Week {selectedWeek} Report ***
                            💰 £{(weeklyData.find((week) => week.week === selectedWeek)?.totalCost || 0).toFixed(2)} ***
                            ✅ {weeklyData.find((week) => week.week === selectedWeek)?.totalJobs || 0} jobs done ***
                            ⚠️ {weeklyData.find((week) => week.week === selectedWeek)?.jobsWithoutCost || 0} jobs with no costings ***
                            🔗 {weeklyData.find((week) => week.week === selectedWeek)?.totalSubbed || 0} jobs Sub-Contracted ***
                        </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                        <thead>
                            <tr style={{ backgroundColor: isLightTheme ? '#ddd' : '#555', color: isLightTheme ? '#333' : '#fff' }}>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('date')}>Date</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('job')}>Job</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('route')}>Route</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('driver')}>Driver</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('truck')}>Truck</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }}>Costings</th>
                            </tr>
                        </thead>
                        <tbody>
                        {chartData.map((job, index) => (
                            <React.Fragment key={index}>
                                <tr
                                    style={{
                                        backgroundColor: job.is_subbed
                                            ? 'rgba(252, 186, 63, 0.8)'
                                            : isLightTheme
                                                ? index % 2 === 0
                                                    ? '#f9f9f9'
                                                    : '#eaeaea'
                                                : index % 2 === 0
                                                    ? '#3a3a3a'
                                                    : '#2e2e2e',
                                        color: job.is_subbed ? '#333' : isLightTheme ? '#333' : '#ddd',
                                    }}
                                >
                                    {["date", "job", "route", "driver", "truck"].map((field) => (
                                        <td
                                            key={field}
                                            style={{
                                                ...styles.td,
                                                borderBottom: isLightTheme ? "2px solid #000" : "1px solid #ddd",
                                            }}
                                            onClick={() => handleFilter(field, job[field] || "-----")}
                                        >
                                            {job[field] || "-----"}
                                        </td>
                                    ))}
                                    <td
                                        style={{
                                            ...styles.td,
                                            borderBottom: isLightTheme ? "2px solid #000" : "1px solid #ddd",
                                        }}
                                    >
                                        {job.costings ? `£${job.costings}` : "-----"}
                                    </td>
                                </tr>
                                {job.is_subbed && (
                                    <tr style={{ height: '10px', borderBottom: isLightTheme ? '2px solid #000' : '1px solid #ddd' }}>
                                        <td colSpan={6} style={{ backgroundColor: isLightTheme ? '#f9f9f9' : '#3a3a3a', textAlign: 'center', padding: '4px 16px' }}>
                                            <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                <strong style={{ marginRight: 24 }}>Subbed Job Details:</strong>
                                                <span style={{ marginRight: 24 }}>Company: <strong>{job.subbed_company || 'N/A'}</strong></span>
                                                <span style={{ marginRight: 24 }}>Price: <strong>£{job.subbed_price || 'N/A'}</strong></span>
                                                <span>Ref: <strong>{job.subbed_ref || 'N/A'}</strong></span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}

                        </tbody>
                    </table>
                </div>
            )}


            {/* Monthly Totals */}
            {selectedWeek === 'month' && (
                <div style={{
                    flex: 1,
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    backgroundColor: isLightTheme ? '#f0f0f0' : '#2a2a2a',
                    borderRadius: '10px',
                    boxShadow: isLightTheme
                        ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                        : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                    maxWidth: '70vw',
                    margin: '20px auto',
                    border: isLightTheme ? '1px solid #ddd' : '1px solid #444'
                }}>
                        <h3
                            onClick={() => isFiltered ? removeFilter() : setSelectedWeek(null) }
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: isLightTheme ? '#333' : '#fff',
                                marginBottom: '15px',
                                cursor: 'pointer',
                                justifyContent: 'space-between',
                            }}
                        >
                            📊 Monthly Report **
                            💰 £{(monthlyTotal?.cost || 0).toFixed(2)} **
                            ✅ {monthlyTotal?.jobs || 0} jobs done **
                            ⚠️ {monthlyTotal?.noCost || 0} jobs with no costings **
                            🔗 {monthlyTotal?.subbed || 0} jobs Sub-Contracted **
                        </h3>
                    <Button
                        onClick={() => {downloadReportCSV(chartData);}}
                        style={{
                            backgroundColor: isLightTheme ? '#4CAF50' : '#66bb6a',
                            color: '#fff',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            marginBottom: '15px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: isLightTheme
                                ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                                : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                            transition: 'background-color 0.3s',
                        }}
                    >
                        Download CSV
                    </Button>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                        <thead>
                            <tr style={{ backgroundColor: isLightTheme ? '#ddd' : '#555', color: isLightTheme ? '#333' : '#fff' }}>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('date')}>Date</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('job')}>Job</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('route')}>Route</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('driver')}>Driver</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }} onClick={() => sortByColumn('truck')}>Truck</th>
                                <th style={{ ...styles.th, borderBottom: theme === 'light' ? '2px solid #000' : '1px solid #ddd' }}>Costings</th>
                            </tr>
                        </thead>
                        <tbody>
                        {chartData.map((job, index) => (
                            <React.Fragment key={index}>
                                <tr
                                    style={{
                                        backgroundColor: job.is_subbed
                                            ? 'rgba(252, 186, 63, 0.8)'
                                            : isLightTheme
                                                ? index % 2 === 0
                                                    ? '#f9f9f9'
                                                    : '#eaeaea'
                                                : index % 2 === 0
                                                    ? '#3a3a3a'
                                                    : '#2e2e2e',
                                        color: job.is_subbed ? '#333' : isLightTheme ? '#333' : '#ddd',
                                    }}
                                >
                                    {["date", "job", "route", "driver", "truck"].map((field) => (
                                        <td
                                            key={field}
                                            style={{
                                                ...styles.td,
                                                borderBottom: isLightTheme ? "2px solid #000" : "1px solid #ddd",
                                            }}
                                            onClick={() => handleFilter(field, job[field] || "-----")}
                                        >
                                            {job[field] || "-----"}
                                        </td>
                                    ))}
                                    <td
                                        style={{
                                            ...styles.td,
                                            borderBottom: isLightTheme ? "2px solid #000" : "1px solid #ddd",
                                        }}
                                    >
                                        {job.costings ? `£${job.costings}` : "-----"}
                                    </td>
                                </tr>
                                {job.is_subbed && (
                                    <tr style={{ height: '10px', borderBottom: isLightTheme ? '2px solid #000' : '1px solid #ddd' }}>
                                        <td colSpan={6} style={{ backgroundColor: isLightTheme ? '#f9f9f9' : '#3a3a3a', textAlign: 'center', padding: '4px 16px' }}>
                                            <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                <strong style={{ marginRight: 24 }}>Subbed Job Details:</strong>
                                                <span style={{ marginRight: 24 }}>Company: <strong>{job.subbed_company || 'N/A'}</strong></span>
                                                <span style={{ marginRight: 24 }}>Price: <strong>£{job.subbed_price || 'N/A'}</strong></span>
                                                <span>Ref: <strong>{job.subbed_ref || 'N/A'}</strong></span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}

                        </tbody>
                    </table>
                </div>
            )}



            {/* Overview Report */}
            {!selectedWeek && (
                <div style={{
                    flex: 1,
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    backgroundColor: isLightTheme ? '#f0f0f0' : '#2a2a2a',
                    borderRadius: '10px',
                    boxShadow: isLightTheme 
                        ? '0px 4px 8px rgba(0, 0, 0, 0.1)' 
                        : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                    maxWidth: '600px',
                    margin: '20px auto',
                    border: isLightTheme ? '1px solid #ddd' : '1px solid #444'
                }}>
                    <h3 style={{ 
                        textAlign: 'left', 
                        fontSize: '20px', 
                        fontWeight: 'bold', 
                        color: isLightTheme ? '#333' : '#fff', 
                        marginBottom: '15px' 
                    }}>
                        📊 Weekly Report
                    </h3>
                    {weeklyData?.length > 0 ? (
                        weeklyData.map(({ week, totalJobs, totalCost, jobsWithoutCost, totalSubbed }) => (
                            <div key={week}
                                className="week-container"
                                onClick={() => handleSelectWeek(week)}
                                style={{
                                    marginBottom: '15px',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    backgroundColor: isLightTheme ? '#f9f9f9' : '#3a3a3a',
                                    borderLeft: `5px solid ${isLightTheme ? '#4CAF50' : '#66bb6a'}`,
                                    boxShadow: isLightTheme 
                                        ? '0 2px 4px rgba(0, 0, 0, 0.1)' 
                                        : '0 2px 4px rgba(255, 255, 255, 0.1)',
                                    width: '100%'
                            }}>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', color: isLightTheme ? '#333' : '#ddd' }}>
                                    Week {week}
                                </div>
                                <div style={{ color: isLightTheme ? '#666' : '#bbb', marginTop: '5px' }}>
                                    ✅ {totalJobs} jobs done
                                </div>
                                <div style={{ color: isLightTheme ? '#666' : '#bbb' }}>
                                    💰 £{(totalCost || 0).toFixed(2)} total costings
                                </div>
                                <div style={{ color: '#ff5722', fontWeight: 'bold' }}>
                                    ⚠️ {jobsWithoutCost} jobs with no costings
                                </div>
                                <div style={{ color: '#dba903ff', fontWeight: 'bold' }}>
                                    🔗 {totalSubbed} jobs subbed
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={{ 
                            textAlign: 'left', 
                            fontSize: '16px', 
                            color: isLightTheme ? '#888' : '#bbb', 
                            fontStyle: 'italic' 
                        }}>
                            No data available for the selected date range.
                        </p>
                    )}
        
                    {/* Monthly Totals */}
                    {monthlyTotal && (
                        <div
                        className="week-container"
                        onClick={() => handleSelectWeek('month')}
                        style={{
                            marginTop: '20px',
                            padding: '15px',
                            borderRadius: '8px',
                            backgroundColor: isLightTheme ? '#f9f9f9' : '#3a3a3a',
                            borderLeft: `5px solid ${isLightTheme ? '#2196F3' : '#64b5f6'}`,
                            boxShadow: isLightTheme 
                                ? '0 2px 4px rgba(0, 0, 0, 0.1)' 
                                : '0 2px 4px rgba(255, 255, 255, 0.1)',
                            width: '100%'
                        }}>
                            <h4 style={{ 
                                fontSize: '18px', 
                                fontWeight: 'bold', 
                                color: isLightTheme ? '#333' : '#ddd', 
                                marginBottom: '10px' 
                            }}>
                                📅 Monthly Totals
                            </h4>
                            <div style={{ color: isLightTheme ? '#666' : '#bbb' }}>
                                ✅ Total Jobs: {monthlyTotal.jobs || 0}
                            </div>
                            <div style={{ color: isLightTheme ? '#666' : '#bbb' }}>
                                💰 Total Costings: £{(monthlyTotal.cost || 0).toFixed(2)}
                            </div>
                            <div style={{ color: '#ff5722', fontWeight: 'bold' }}>
                                ⚠️ Jobs with No Costings: {monthlyTotal.noCost || 0}
                            </div>
                            <div style={{ color: '#dba903ff', fontWeight: 'bold' }}>
                                🔗 Jobs Subbed: {monthlyTotal.subbed || 0}
                            </div>
                        </div>
                    )}
                </div>
            )}
    
            {/* Render the RequestPassword modal only if openRequestPassword is true */}
            {openRequestPassword && (
                <RequestPassword
                    theme={theme}
                    isOpen={openRequestPassword}
                    setIsOpen={setOpenRequestPassword}
                    returnData={(value) => handlePasswordSubmission(value)}
                />
            )}
    
            {/* Commented out the PieChart for now */}
            {/* <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Pie data={chartData} />
            </div> */}
        </div>
    );
    
};

const styles = {
    th: {
        padding: '10px',
        textAlign: 'center',
        cursor: 'pointer'
    },
    td: {
        padding: '10px',
        cursor: 'pointer',
    }
};

export default WeeklyReport;
